import { useEffect, useState } from "react";
import { BbotButton, Checkbox, SettingsCard } from "top-component-library";
import { AdvancedOptionsHeading, SaveRow } from "../styles";
import { toRawHostnameConfig } from "../utils";
import ArchiveBosDialog from "./ArchiveBosDialog";
import TemporarilyDisableOrderingCard from "./TemporarilyDisableOrderingCard";

// Mirrors edit-hostname-configs.html's "Advanced Options" block. Dialog-backed
// addNewHostname/enable-theme/enable-builder actions are added separately; this
// covers the pieces that act directly on the current hostnameConfig.
const AdvancedOptionsSection = ({
  hostnameConfig,
  isAdmin,
  isBbotAdmin,
  customerName,
  onSave,
  onEnableLegacyBos,
  onArchiveHostname,
  onTemporarilyDisableOrdering,
  fetchTemporaryDisableText,
}) => {
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  // canEdit is the client-recomputed final editability (raw is_editable AND role
  // rules, see utils.ts's userIsAllowedToEdit) - the legacy controller overwrites
  // its own is_editable in place with this same final value, so every ng-if/
  // ng-disabled on selectedHostnameConfig.is_editable in the template actually
  // means canEdit here, not the raw server flag.
  const { hostname, configType, canEdit, redirect_to_hostname_config: redirectToHostnameConfig } = hostnameConfig;

  return (
    <>
      <AdvancedOptionsHeading>Advanced Options</AdvancedOptionsHeading>
      {isAdmin && (
        <SettingsCard title="Redirect to another domain">
          <div>
            <p>
              (Admin only, <strong>ability to edit is coming soon!</strong>) Allows you to make all requests to{" "}
              <strong>{hostname}</strong> show a different Bbot Customer&apos;s BOS. They will still see{" "}
              <strong>{hostname}</strong> in the address bar.
            </p>
            {redirectToHostnameConfig ? (
              <p>
                <strong>Guests will be redirected to {redirectToHostnameConfig.hostname}</strong> when visiting{" "}
                <strong>{hostname}</strong>
              </p>
            ) : (
              <p>
                Guests will be shown <strong>{hostname}</strong> as configured above.
              </p>
            )}
          </div>
        </SettingsCard>
      )}

      <TemporarilyDisableOrderingCard
        hostnameConfig={hostnameConfig}
        onTemporarilyDisableOrdering={onTemporarilyDisableOrdering}
        fetchTemporaryDisableText={fetchTemporaryDisableText}
      />

      {configType !== "notConfigured" && <ShowAccountSignInCard hostnameConfig={hostnameConfig} onSave={onSave} />}

      {isBbotAdmin && configType !== "notConfigured" && (
        <ShowLandingPageFooterCard hostnameConfig={hostnameConfig} onSave={onSave} />
      )}

      {isBbotAdmin && (
        <SettingsCard title="Enable Legacy BOS Configuration">
          <div>
            <p>
              (TOP by DoorDash Admin only) Allows you to enable legacy BOS for <strong>{hostname}</strong> if{" "}
              <strong>{hostname}</strong> has legacy assets.
            </p>
            <p>
              Click to enable legacy BOS for <strong>{hostname}</strong>.
            </p>
            <BbotButton type="primary" onClick={() => onEnableLegacyBos(hostname)} disabled={!canEdit}>
              Enable
            </BbotButton>
          </div>
        </SettingsCard>
      )}

      {isBbotAdmin && configType === "bosBuilder" && (
        <SettingsCard title="Archive BOS">
          <div>
            <p>
              (TOP by DoorDash Admin only) Allows you to archive the BOS for <strong>{hostname}</strong>.
            </p>
            <p>
              Click to archive <strong>{hostname}</strong>. This means <strong>{hostname}</strong> will redirect to
              bbot.menu and will not show as editable on any customers&apos; BOS Settings page. Only this customer will
              be able to unarchive this BOS back to its original state.
            </p>
            <BbotButton type="primary" onClick={() => setShowArchiveDialog(true)}>
              Archive BOS
            </BbotButton>
          </div>
          <ArchiveBosDialog
            visible={showArchiveDialog}
            hostname={hostname}
            customerName={customerName}
            onCancel={() => setShowArchiveDialog(false)}
            onConfirm={async () => {
              const succeeded = await onArchiveHostname(hostname);
              if (succeeded) setShowArchiveDialog(false);
            }}
          />
        </SettingsCard>
      )}
    </>
  );
};

export default AdvancedOptionsSection;

// The legacy page shows this card unconditionally, but a not-yet-configured
// hostname has no customer_pretty_name/switch_text for editHostnameConfig to
// save (both are required fields server-side), which would 500. Hidden for
// configType === "notConfigured" above rather than porting that crash.
const ShowAccountSignInCard = ({ hostnameConfig, onSave }) => {
  const [showAccountSignIn, setShowAccountSignIn] = useState(hostnameConfig.show_account_sign_in);

  useEffect(() => {
    setShowAccountSignIn(hostnameConfig.show_account_sign_in);
  }, [hostnameConfig]);

  return (
    <SettingsCard title="Show Account Sign-in">
      <div>
        <p>
          Allows you to hide the account sign-in option from the top right-hand corner of{" "}
          <strong>{hostnameConfig.hostname}</strong> and its ordering pages.
        </p>
        <Checkbox checked={showAccountSignIn} onChange={(e) => setShowAccountSignIn(e.target.checked)}>
          Check to show the account sign-in option for this hostname&apos;s BOS.
        </Checkbox>
        <SaveRow>
          <BbotButton
            type="primary"
            onClick={() =>
              onSave(
                { ...toRawHostnameConfig(hostnameConfig), show_account_sign_in: showAccountSignIn },
                "Hostname settings updated."
              )
            }
          >
            Save
          </BbotButton>
        </SaveRow>
      </div>
    </SettingsCard>
  );
};

const ShowLandingPageFooterCard = ({ hostnameConfig, onSave }) => {
  const [showBbotFooter, setShowBbotFooter] = useState(hostnameConfig.show_bbot_footer);

  useEffect(() => {
    setShowBbotFooter(hostnameConfig.show_bbot_footer);
  }, [hostnameConfig]);

  return (
    <SettingsCard title="Show Landing Page Footer">
      <div>
        <p>
          (TOP by DoorDash Admin only) Allows you to remove the &quot;Powered by DoorDash&quot; footer from the live
          version of <strong>{hostnameConfig.hostname}</strong>.
        </p>
        <Checkbox checked={showBbotFooter} onChange={(e) => setShowBbotFooter(e.target.checked)}>
          Check to show &quot;Powered by DoorDash&quot; footer from this hostname&apos;s BOS.
        </Checkbox>
        <SaveRow>
          <BbotButton
            type="primary"
            onClick={() =>
              onSave(
                { ...toRawHostnameConfig(hostnameConfig), show_bbot_footer: showBbotFooter },
                "Hostname settings updated."
              )
            }
          >
            Save
          </BbotButton>
        </SaveRow>
      </div>
    </SettingsCard>
  );
};
